import { useNavigate } from "react-router-dom";
import { SignIn, SignOut, Prohibit, Receipt, ArrowsClockwise, Key } from "@phosphor-icons/react";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const CHANNEL_LABELS = { walk_in: "Walk-in", phone: "Phone", website: "Website", easystay_app: "EasyStay App" };

export function BookingRow({ b, fmt, onAction, onInvoice, onSendKey }) {
  const navigate = useNavigate();
  return (
    <tr className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`booking-row-${b.id}`}>
      <td className="p-3">
        <div className="font-medium">{b.guest_name}</div>
        <div className="text-xs text-muted-foreground">{b.guest_phone}</div>
      </td>
      <td className="p-3">{b.room_name} <span className="text-xs text-muted-foreground">· {b.room_type}</span></td>
      <td className="p-3 text-muted-foreground">{b.check_in} → {b.check_out}</td>
      <td className="p-3">{b.nights}</td>
      <td className="p-3 text-right font-medium">{fmt(b.total)}</td>
      <td className="p-3"><StatusBadge status={b.status} testId={`booking-status-${b.id}`} /></td>
      <td className="p-3" data-testid={`booking-channel-${b.id}`}>
        <div className="text-xs font-medium">{CHANNEL_LABELS[b.channel] || "Walk-in"}</div>
        {b.contactless && (
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`digital-key-${b.id}`}>
            <Key size={11} /> {b.digital_key || "—"} · {b.key_status === "sent" ? "key sent" : "key pending"}
          </div>
        )}
      </td>
      <td className="p-3" data-testid={`booking-sync-${b.id}`}>
        {b.sync_status === "synced" ? (
          <span className="flex items-center gap-1 text-xs text-status-confirmed" title={`EasyStay ref: ${b.external_id}`}>
            <ArrowsClockwise size={13} weight="bold" /> EasyStay
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Local</span>
        )}
      </td>
      <td className="p-3">
        <div className="flex justify-end gap-1.5">
          {b.contactless && b.key_status !== "sent" && b.status !== "cancelled" && (
            <Button size="sm" variant="outline" data-testid={`send-key-${b.id}`} onClick={() => onSendKey(b.id)}
              className="transition-colors hover:border-primary hover:text-primary">
              <Key size={14} className="mr-1" /> Send key
            </Button>
          )}
          {["confirmed", "pending"].includes(b.status) && (
            <Button size="sm" variant="outline" data-testid={`check-in-${b.id}`} onClick={() => onAction(b.id, "check-in", "Guest checked in")}
              className="transition-colors hover:border-status-checked-in hover:text-status-checked-in">
              <SignIn size={14} className="mr-1" /> Check in
            </Button>
          )}
          {b.status === "checked_in" && (
            <Button size="sm" variant="outline" data-testid={`check-out-${b.id}`} onClick={() => onAction(b.id, "check-out", "Guest checked out")}
              className="transition-colors hover:border-status-pending hover:text-status-pending">
              <SignOut size={14} className="mr-1" /> Check out
            </Button>
          )}
          {!b.invoice_id && !["cancelled"].includes(b.status) && (
            <Button size="sm" variant="outline" data-testid={`create-invoice-${b.id}`} onClick={() => onInvoice(b.id)}
              className="transition-colors hover:border-primary hover:text-primary">
              <Receipt size={14} className="mr-1" /> Invoice
            </Button>
          )}
          {b.invoice_id && (
            <Button size="sm" variant="outline" data-testid={`view-invoice-${b.id}`} onClick={() => navigate(`/invoices/${b.invoice_id}`)}
              className="transition-colors hover:border-primary hover:text-primary">
              <Receipt size={14} className="mr-1" /> View
            </Button>
          )}
          {!["cancelled", "checked_out"].includes(b.status) && (
            <Button size="sm" variant="outline" data-testid={`cancel-booking-${b.id}`} onClick={() => onAction(b.id, "cancel", "Booking cancelled")}
              className="transition-colors hover:border-destructive hover:text-destructive">
              <Prohibit size={14} />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
